/* KuraCo storefront — bootstrap. Loads after pubsub.js + constants.js (Dawn primitives).
   Defines <kuraco-particles>, <kuraco-cart-popover>, <kuraco-product-card>, <kuraco-header>.
   Talks to Shopify Cart API: /cart/add.js, /cart/change.js, /cart.js. */

const KC = window.KC || {};

// Money formatter — INR by default; falls back to shop.money_format if non-INR.
const formatMoney = (cents) => {
  if (KC.currency === 'INR') {
    return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: KC.currency || 'INR',
      maximumFractionDigits: 0
    }).format(cents / 100);
  } catch {
    return '₹' + (cents / 100).toFixed(0);
  }
};

const kcEvents = (() => {
  const t = new EventTarget();
  return {
    on:  (n, fn) => t.addEventListener(n, fn),
    off: (n, fn) => t.removeEventListener(n, fn),
    emit:(n, d)  => t.dispatchEvent(new CustomEvent(n, { detail: d }))
  };
})();
window.kcEvents = kcEvents;

async function kcCartFetch() {
  const r = await fetch(`${window.Shopify ? window.Shopify.routes.root : '/'}cart.js`, {
    headers: { 'Accept': 'application/json' }
  });
  return r.json();
}

async function kcCartAdd(variantId, quantity = 1) {
  const r = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity }] })
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'Add to cart failed');
  }
  return kcCartFetch();
}

async function kcCartChange(key, quantity) {
  const r = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  return r.json();
}

window.kcCart = { fetch: kcCartFetch, add: kcCartAdd, change: kcCartChange };

/* ─────────────────────────────────────────── Particles ─── */
class KuracoParticles extends HTMLElement {
  static observedAttributes = ['data-seed', 'data-count'];
  static _aspectCache = new Map();

  connectedCallback() {
    if (this._mounted) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.style.display = 'none';
      return;
    }

    if (this.hasAttribute('data-eager')) {
      this.scatter();
      return;
    }

    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        this.scatter();
        io.disconnect();
      }
    }, { rootMargin: '80px' });
    io.observe(this);
  }

  static async loadAspects(srcs) {
    const cache = KuracoParticles._aspectCache;
    const todo = srcs.filter(s => !cache.has(s));
    await Promise.all(todo.map(async (src) => {
      try {
        const txt = await fetch(src).then(r => r.text());
        const m = txt.match(/viewBox="([^"]+)"/);
        if (m) {
          const parts = m[1].trim().split(/[\s,]+/).map(Number);
          const w = parts[2], h = parts[3];
          cache.set(src, { w, h });
        } else {
          cache.set(src, { w: 1, h: 1 });
        }
      } catch {
        cache.set(src, { w: 1, h: 1 });
      }
    }));
    return cache;
  }

  async scatter() {
    if (this._scattering) return;
    this._scattering = true;
    if (!this._resizeBound) {
      this._resizeBound = true;
      let raf = 0;
      this._ro = new ResizeObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.rescatter());
      });
      this._ro.observe(this);
      window.addEventListener('resize', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.rescatter());
      });
    }
    await this.render();
    this._mounted = true;
    this._scattering = false;
  }

  async rescatter() {
    if (this._scattering) return;
    this._scattering = true;
    this.replaceChildren();
    await this.render();
    this._scattering = false;
  }

  async render() {
    const seed   = parseInt(this.dataset.seed || '11', 10);
    const count  = parseInt(this.dataset.count || '14', 10);
    const colors = (this.dataset.colors || 'indian-ocean red-ochre paddyfield-lime vermillion submarine').trim().split(/\s+/);
    const [smin, smax] = (this.dataset.size || '50,110').split(',').map(Number);
    const opacity = parseFloat(this.dataset.opacity || '0.95');
    const tier    = (this.dataset.tier || 'shape');
    const avoidSelector = this.dataset.avoidElements || '';

    const rng = (() => {
      let s = seed >>> 0;
      return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 0xffffffff; };
    })();

    const illoKeys = Object.keys(KuracoParticles.illos);
    let maxH = 1;
    if (tier === 'shape') {
      for (const k of illoKeys) { const d = KuracoParticles.illos[k]; if (d.h > maxH) maxH = d.h; }
    }

    const sectionRect = this.getBoundingClientRect();
    const obstacles = avoidSelector
      ? [...document.querySelectorAll(avoidSelector)].map(el => el.getBoundingClientRect())
      : [];
    const intersects = (a, b) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const sBase = smin + rng() * (smax - smin);
      const rot   = (rng() - 0.5) * 180;
      const tok   = colors[Math.floor(rng() * colors.length)];

      let w = sBase, h = sBase, src = '';
      if (tier === 'shape') {
        const d = KuracoParticles.illos[illoKeys[Math.floor(rng() * illoKeys.length)]];
        src = d.uri;
        h = sBase * (d.h / maxH);
        w = h * (d.w / d.h);
      }

      // Pick a center that doesn't collide with content. Allow some overflow off the section edges.
      let cxPct = 50, cyPct = 50, fits = obstacles.length === 0;
      const overflowPct = tier === 'shape' ? 12 : 8;
      const xMin = -overflowPct, xMax = 100 + overflowPct;
      const yMin = -overflowPct, yMax = 100 + overflowPct;
      for (let t = 0; t < (obstacles.length ? 80 : 1); t++) {
        const tx = xMin + rng() * (xMax - xMin);
        const ty = yMin + rng() * (yMax - yMin);
        const cx = sectionRect.left + (tx / 100) * sectionRect.width;
        const cy = sectionRect.top  + (ty / 100) * sectionRect.height;
        const candidate = {
          left: cx - w / 2, right: cx + w / 2,
          top:  cy - h / 2, bottom: cy + h / 2
        };
        let collides = false;
        for (const o of obstacles) {
          if (intersects(candidate, o)) { collides = true; break; }
        }
        if (!collides) { cxPct = tx; cyPct = ty; fits = true; break; }
      }
      if (!fits) continue;

      const span = document.createElement('span');
      span.className = 'kc-particle';

      if (tier === 'dust') {
        const sides = 5 + Math.floor(rng() * 4);
        const angleStep = (Math.PI * 2) / sides;
        const pts = [];
        for (let k = 0; k < sides; k++) {
          const ang = angleStep * k + (rng() - 0.5) * angleStep * 0.6;
          const r = 0.5 + rng() * 0.5;
          pts.push(`${(50 + Math.cos(ang) * r * 50).toFixed(1)}% ${(50 + Math.sin(ang) * r * 50).toFixed(1)}%`);
        }
        const rotDust = rng() * 360;
        span.style.cssText = `
          left:${cxPct}%; top:${cyPct}%;
          width:${sBase}px; height:${sBase}px;
          transform: translate(-50%,-50%) rotate(${rotDust}deg);
          opacity:${opacity};
          background: var(--${tok});
          clip-path: polygon(${pts.join(', ')});
        `;
      } else {
        span.style.cssText = `
          left:${cxPct}%; top:${cyPct}%;
          width:${w}px; height:${h}px;
          transform: translate(-50%,-50%) rotate(${rot}deg);
          opacity:${opacity};
          color: var(--${tok});
          -webkit-mask-image:url(${src}); mask-image:url(${src});
        `;
      }
      frag.appendChild(span);
    }
    this.appendChild(frag);
  }
}

KuracoParticles.illos = {
  'bayleaf':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMy45NyAzOC4yMiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik00Ljg3LDEuMDZsMS4zNS0xLjA2YzQsNC42NSw4LjcsMTMuNyw3LjU5LDI1LjdsLTYuMjMsMTEuNTMtMS42NSwxLTIuNTMtMi42NUwuMTYsMjMuNzZDLS45LDEzLjcsMy41MSw0LjY1LDQuODcsMS4wNloiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:13.97,h:38.22},
  'beans-sliced':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMS41MiAyMy43Ij4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTTQuNDEsMi4zNUwxNC4yOSwwYzMuMjMsMi45NCw1LjY1LDYuMjMsNy4yMyw5Ljc2LS41Myw0LTEuNjUsNy42NS0zLjM1LDExLjA2bC0zLjEyLDIuODgtMTAuNTgtMS4wNkwwLDE3LjI5QzAsMTEuOTQuOTQsNi40MSw0LjQxLDIuMzVaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:21.52,h:23.7},
  'beans':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1LjQ3IDM4LjIyIj4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTTAsMzUuNDZsLjc2LTIuMjkuNjUtLjEyLS43Ni0zLjU5LDEuNTktMy4xMmMuMTItLjg4LS41My0yLjEyLTEuMjMtMi43MWwtLjEyLTIuMTIsMS42NS0yLjY1LTEuNTMtMS40MS0uMDYtMS4xMiwxLTQuNTMtMS4yOS0xLjA2LjY1LTUuMjNDLjI5LDMuOTQtLjE4LDEuNjUuNjUuMTJsMi41OS0uMTIsMi4yNCwxNC0uMTgsMTIuNjRjLS40MSwzLjY1LTEuMDYsOC4xMi0xLjgyLDEwLjgybC0yLjI5Ljc2LTEuMTgtMi43NloiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:5.47,h:38.22},
  'cabbage-shredded':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNC4zNSAzOC4zNCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMS40Ny4wNmwyLjg4LDE0LjU4LTIuMjksMTMuNzYtLjU5LDEuNDdjLTQuNDcsMS45NC03LjcsNi40MS04LjQxLDguNDdsLTMuMDYtLjA2YzIuMDYtMy45NCw0LjUzLTcuODgsOS40Ny0xMC4xN2wyLTEzLjQ3TDguMjMsMGwzLjIzLjA2WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:14.35,h:38.34},
  'cabbage':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0My40NSAzOC4wOCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xNi40LDIuNzFMMjQuNjQsMGwzLjQ3LDIuNTMtLjI0LDEuNTMsMTEuODgsNi4xNywzLjcxLDMuNTktMiw5LjI5LTYuMTcsMTIuMjljLTIuODgsMS45NC0xNi42NCw0LTIwLjUyLDEuNTlsLTEuMjkuMzVMLjA1LDI4LjE3Qy0uMjQsMTkuNzYuNywxMi4zNSwxLjUzLDExLjExbDYuMDYsMS43MWMyLjA2LTMuMTgsNS43Ni03Ljc2LDguODItMTAuMTFaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:43.45,h:38.08},
  'carrot-chopped':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMC40MSAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0zLjM1LDIzLjUybC0xLjg4LTIuMzVMMCwzLjk0LDEwLjQ3LDBjNC4wNiwwLDguMTcsMS44OCw5Ljk0LDMuMjlsLTQuNzEsMjAuMjNIMy4zNVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:20.41,h:23.52},
  'carrot-diced':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMi41OCAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik01LjUzLDIuMjNsMy4zNS0yLjIzaDEzLjE3YzEuOTQsOC0xLjk0LDE0Ljk0LTQuMzUsMTguNy0zLjgyLDMtMTEuMTcsNC41OS0xNi45NCw0LjgybC0uNzYtMS4yM0w1LjUzLDIuMjNaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:22.58,h:23.52},
  'carrot-sliced':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzNC4zMyAzMS4yOCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xOCwzMS4yOGwtMTEuODgtNC40MUwwLDEzLjI4LDcuNDEsMS45M2M0LjI5LTMuNzEsMTYuNTItMS4zNSwyMC40MSwxLDQuMTgsNC41Myw3LjgyLDExLjU4LDYuMDYsMTcuMjlsLTEyLjY0LDExLjA2aC0zLjIzWiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:34.33,h:31.28},
  'carrot':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMC41OCA2NC44MSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik04LDIxLjA1bDQuNjUtLjQxLjA2LTEuMTItNi41OS0xMC44Mi41OS0xLjg4LDcuNDEtMi40NywzLjQxLDEtMi4zNSwxMS41OEwyNC44OCwwbDMuNTMsMS4xOCwyLjE4LDQuODItLjE4LDEuNzFjLTIuMjMsMS4zNS0xMi41OCwxMC4zNS0xNC42NCwxMi43LDEuNjUuMTgsMy44Mi43Niw1LjA2LDEuNjVsLjUzLDQuMThMMy43Niw2NC41MWwtMy43Ni4yOWMwLTEyLjExLDMuMTgtMjkuMTcsOC00My43NVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:30.58,h:64.81},
  'cashew':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNC43NiAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik02LjgyLDEyLjIzbC0uMjksMS40MS0uMDYuMDZjLjc2LDEuMjMsMi43NiwxLjgyLDMuNDEsMS41M2wyLjY1LjA2LjcxLjU5Yy4yNCwyLjE4LS40Nyw0LjI5LTEuNTksNS43bC01LjM1LDEuOTQtMi0uNTljLTIuMzUtMi4wNi0zLjM1LTMuNTMtNC4yOS03LjQxbC40MS02LjI5QzIuNTkuNjUsNy40NywwLDkuNywwczQuNjUsMS4yOSw1LjA2LDIuMThsLS40MSwzLjI5LTMuOTQsNC41M2MtMS43MS0uMzUtMi44MiwxLTMuNTksMi4yM1oiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:14.76,h:23.52},
  'cauliflower':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyOS44MiAzOC4yMiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMy45NCwxMy44OGwyLjEyLTQuMDZjLTEuMTItLjI5LTIuMDYtLjM1LTMuMDYtLjI5bC0yLjQxLTEuNDcsMi40MS02Ljk0LDIuMjktMS4xMmMyLjcxLjk0LDcuMzUsMS43Niw4LjUzLDEuNTlsMywyLjQxLTMuNzYsOC4xN2MtMS4xMi0uNzYtMi4yNC0xLjI5LTMuNzEtMS41M2wtMS41OSw2LjEyLDEuODIuNzYsMi4wNi4yNC0uNTktMy41OSwxLjQ3LS45NCwxLjE4Ljc2Yy4yNC0xLjU5Ljk0LTMuOTQsMS43MS00LjdsMS4zNS4xOCwyLjc2LDIuMTJjLS4wNiwxLjY1LS40MSwzLjA2LS45NCw0LjQ3bDEuMjQuNzEtLjUzLDQuNTMuMTIsMi43Ni0uNzYsMS40Ny0zLjY1LjI5LTMuNDctLjUzYy0uNDctLjA2LS44OC0uNzYtLjA2LTUuMjNsLTQuNDEtLjA2LTEuNDcsMTMuMjMtLjg4LDVoLTIuODJsLTMuMTItMi44Mi0yLjg4LS43MS4yOS0xLjk0LDcuMjktMTQuMTdjLS44Mi0xLjcxLTIuMjMtMy4yOS0zLjcxLTQuNDdsLTEuMDYsMS44Mi0xLjU5LDUuOTQtMi45NC0xLTMtMi40Ny41My0yLjk0LTEuNzEtMS4yOSwzLjEyLTcuMDYsMS44Mi0uNDFjMi4xOCwxLjE4LDUuNDcsMy40Nyw2LjQ3LDQuODhsLS4yOS40MWMuOTQuNjUsMi4xMiwxLjI5LDIuODIsMS44OFoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:29.82,h:38.22},
  'chana-split-dal':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNy4wNSAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0uMjMsMjMuNTJsLS4yMy0uNjUsNS40Ny0xMS4wNkwxNC40MSwwbDEuODIsMS4xMmMtLjg4LDIuMjMtMS4wNiw0LjctLjc2LDYuNzZsMS41OSw5LjIzLTMuMjMsNC41OWMtNC4xMiwxLjQxLTYuODIsMS44Mi0xMy41OCwxLjgyWiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:17.05,h:23.52},
  'chilli':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNC4yMyA0MS44MSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik00LjI5LDQxLjgxbC4xOC0xLjQ3LDEuNTMtMy42NWMzLjc2LTE0Ljc2LDQuODgtMTUuNTMtMy43Ni0yOC4xN2wtLjA2LTMsMS4yOS0xLjUzTC40MSwxLjk0bC0uNDEtLjY1TDIsMGwyLjU5LDMuNTMsNC4zNS4wNmMxLjcxLDQuMDYsNC4yMyw3LjA2LDUuMjksMTIuMTFsLS4xOCwxMS43Ni04LjQ3LDE0LjM1aC0xLjI5WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:14.23,h:41.81},
  'cinnamon':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2LjM1IDIzLjUyIj4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTTQuOTQsMGwuNTMsMS40MS0uNjUsMi42NS0uMjksOS40MWMuMjksMy44Mi43Niw2LjI5LDEuODIsOS4yOWwtMS4yOS43NmMtLjI0LS4yOS0xLjE4LTItMS41OS0zLjY1bC0uMjMtLjA2Yy0uMDYuNTMsMCwxLjcxLjEyLDIuMjlsLTIuNDcsMS4wNi0uMjktLjUzVjcuMTdjMC0uODgtLjEyLTEuODgtLjU5LTMuNjVsMS41My0uNTljLjI5LjU5LjUzLDEuMjMuNjUsMi4wNmguMjlMMi43Ni40MWwyLjE4LS40MVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:6.35,h:23.52},
  'coriander':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMi41MiAzOC4xMyI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMC44OCwzNi43OGwyLjEyLTUuNy03Ljk0LS45NC43Ni0zLjA2TDAsMjEuNzhsLjA2LTEuMTIsNC43LTUuMTcsMi43MS4yOSwxLjU5LDUuODIsMS4wNi0yLjEyLDIuNzYuMzVjLjc2LjQxLDIsMi41MywyLjIzLDMuOTRsLjc2LTIuNDEtNi42NS03LjUzLDMuODgtMy4yMywyLjA2LS4xOCwyLDQuODIsMS4xOC0uNjUtMS44OC0xMS41MywzLjEyLjI5LS43MS0yLjA2LjE4LTEuMjNjMi43MS0uMjksNS44Mi4yNCw2Ljk0LDFsLS4wNiwxLjM1LTEuNTMsMi45NCwyLjE4LjU5LS4xOCwxLjQxLTQuNjUsNy4zNS0uMzUsMS40MSwxLjI0LTEuMzUsMi42NS40MSwyLjI5LDIuNTMtLjc2LDIuMDZjLTEuMjksMS4xOC01LjE3LDIuMzUtNi44MiwyLjI5bC0uODIsMy4yOSw0LjY1LTIuODIsMi40MS42NS0uMTgsMi4wNiwzLjM1LTMuMTIsMy4xMi41My0xLjU5LDkuNTktNS40Ny0xLjI5Yy0uNDEsMS4yMy0uNTksMS43Ni0uMjksMy4xOGwtLjcxLjc2LTIuNzEtLjA2LTUuMDYtMi41OS0uOTQsMi4yOWMtLjA2LjcxLjEyLDEuOTQtLjQxLDIuNzFsLTQuMTIuODgtLjM1LTEuMzVaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:32.52,h:38.13},
  'curry-leaf':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA4Ljg1IDI3LjM1Ij4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTTUuNzMsMjYuOTNsLTEuNTkuNDFDMi43MywyMy4xNy4zMiwxOC45NC4wMywxNC43NlMyLjA4LDYuMTcsMi43OSwzLjc2bC4yNC0uMDYtLjE4LTMuNywxLjM1LjQxLjI5LDMuMjMuMjktLjEyYzMuODIsNC4yMyw0LjA2LDUuNTMsNC4wNiwxMC40Ny0uMDYsNC44OC0yLjY1LDEwLjgyLTMuMTIsMTIuOTRaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:8.85,h:27.35},
  'foxtail-millet':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA5LjggMjMuNTIiPgogIDxnIGlkPSJMYXllcl8xLTIiIGRhdGEtbmFtZT0iTGF5ZXIgMSI+CiAgICA8cGF0aCBkPSJNLjI3LDEzLjc2Qy0uOTEsOC45NCwyLjAzLDIuMDYsNC4zOC44OGwuNTMtLjg4LDEuNTkuNDFjMS4wNi44Miw0LjA2LDUuNTMsMy4xMiwxMS4yM2wtMi41MywxMS4xMS0yLjgyLjc2LTEuNzEtMS4xMkwuMjcsMTMuNzZaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:9.8,h:23.52},
  'garlic-clove':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMi41OCAyMy42MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xLjE4LDguMjdMMCwuMzNDMy41OS0uMDIsNC42NS0uMzIsNy4yMy42OGMyLjU5LDEsNC40NywyLjI5LDQuNzYsNC4yOWwuNTksMy44Mi0uNzEsNy40N2MtMS42NSw0LjIzLTYuNjUsNy4zNS0xMC41OSw3LjM1bC0xLjI5LTIuMjljLjc2LTEuMjMsMS45NC02LjIzLDEuMTgtMTMuMDZaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:12.58,h:23.62},
  'ginger':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNC4yMyAzOC4yMyI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik05LjU4LDcuNzZjLTEtMS42NS0xLjE4LTEuODgtMS4yMy0zLjY1bC44OC0xLjI5YzIuNTMtMS4yMyw2Ljc2LTIuODIsMTAuNTgtMi44MmwuMjksMS43Ni00LjY1LDEwLjI5LDIuMzUtLjQ3YzIuNzYuNDcsMy43NiwxLjI5LDYuMDYsMy4yOWwuMzUsMS4yM2MtNC4yOSwxLjcxLTcuMjMsNC4xOC0xMC4zNSw2Ljk0bC0uMzUsNS4zNWMyLjU5LS44OCwzLjg4LS44OCw3LjM1LS44MmwuNDEsMS4xOC0zLjIzLDUuNjUtMi40MS43Ni0yLTEuMjNjLTMuMTgsMi42NS02LjIzLDQuMjktMTEuMjksNC4yOWwtLjM1LTEuMjksMy45NC0xMC4wNkwuMjksMjEuMTdsLS4yOS0xLDcuNTktMS44Mi0xLjM1LTEwLjEyLDMuMzUtLjQ3WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:24.23,h:38.23},
  'lobia':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxOC4xMSAyMy41OCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xNC43LDMuMTJsMy40MSw5LjIzLTEuMzUsNS4wNmMtMS4xMiw0LjE4LTMuNDcsNi4xNy04LjI5LDYuMTdsLTcuMTItMS43MS0xLjM1LTUuMTdjMi4zNS0yLjE4LDMuNTMtNCwzLjcxLTUuNDFsLTEuMDYtNC4wNi4xOC0xLjA2TDcuOTQsMGw2Ljc2LDMuMTJaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:18.11,h:23.58},
  'methi':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNy41MiAyOC42NCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0wLDYuMjlsMS4wNi0zLjA2QzIuNDcsMS44Miw2LjQxLDAsOC4xNywwczYsMS4xOCw5LjM1LDIuNDFsLS40MSwyLjk0LTguMzUsMTMuNjQtLjQ3LjA2LjI0LDguMzUtLjI0LjUzLTIuNTkuNzEuMDYtNC4xOC0uNTMtNS40N0wwLDYuMjlaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:17.52,h:28.64},
  'mint':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxOS43IDMwLjM0Ij4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTTQuODIsNC43NmwtLjA2LTIuNDcsMS4xMi0xLjEyLDQuODItMS4xOCwxLjY1LDIuMDYuMjQsMSwxLjQxLS40NywyLjIzLDIuMTJjLjUzLDEuODguNDEsMy4zNS4wNiw1LjI5bDIuNTMsMS4yOS0xLjc2LDUuMjksMi42NS43MWMtLjU5LDMuMTItMyw4LjU5LTguMDYsOS4yOWwuNjUuNzYuMjksMS4yM2MtLjk0LjM1LTEuNzEuNzEtMS44OCwxLjI5bC0yLjQ3LjQ3Yy0uNTMtLjcxLS41OS0xLjU5LS41OS0zLjcxaC0xLjQxbC01LjQ3LTItLjA2LTIuMThjLjQ3LS42NSwxLTEuNDEsMS4xMi0xLjg4QzEuNDEsMjAuMTEtLjA2LDE3LjU4LDAsMTUuN2wxLjk0LTMuMzUtMS0zLjQ3LjgyLTEuMzUsMy4wNi0yLjc2WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:19.7,h:30.34},
  'mung-dal':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMy4xNyAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMS41MywyLjg4bC0xLjEyLDYuMTcsMi43Niw2Yy0uNzEsNS43LTIsNi43LTUuNTMsOC40N0wwLDE0LjM1LjEyLDMuOTRDNC43Ni41OSw2LC4zNSw5LjM1LDBsMi4xOCwyLjg4WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:13.17,h:23.52},
  'nutmeg':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxOS45NCAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMC41OCwwYzIuMjMuODIsNC43LDIuNDcsNy4xNyw0Ljk0LDEuMjMsMy4yMywyLjQ3LDcuMzUsMi4xMiwxMy4yM2wtMi43MSwzLjM1Yy0yLjA2LDEuNDEtMy4wNiwyLTYuMTcsMi0zLjc2LDAtOS44OC0zLjIzLTkuNjQtNS44MmwtMS4zNS0xLjY1TDEuNDcsNi43LDEwLjU4LDBaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:19.94,h:23.52},
  'onion-chopped':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNC4yOSAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0wLDIuNTNMMi40MSwwaDhsMy44OCwyMC40Ni0xLjA2LDMuMDZILjY1bC0uNDctMy40N0wwLDIuNTNaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:14.29,h:23.52},
  'onion-slice':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMS4xNyAzOC4yMiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0yMS4xNywxMC40MXYxMi41M2MtNC4wNiw3LjY1LTExLjQ3LDEyLjE3LTE5Ljk0LDE1LjI5bC0xLjIzLS41OS41My0xLjg4YzUuODItMi44OCwxMC43LTcuNDcsMTQuMzUtMTMuNzYsMy41My05LjIzLDIuNDEtMTYuNDcsMC0yMC45OWwxLjUzLTFjMi4yMywyLjk0LDMuODIsNi40MSw0Ljc2LDEwLjQxWiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:21.17,h:38.22},
  'pea':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMy42IDIzLjU4Ij4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTS42MSw2LjY1TDcuNzIsMGwxNS44OCw2LjIzYy0xLjQxLDcuMjktMy44OCwxNC4yOS04Ljk0LDE3LjM1bC0xMi40MS02LjIzQy4wOCwxNC4yMy0uNjMsMTAuMjMuNjEsNi42NVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:23.6,h:23.58},
  'potato-chopped':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyOC4xNyAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xNCwwbDE0LjE3LDkuNDEtMi40Nyw2Ljc2Yy0zLjY1LDQuMTItNy45NCw2LjUzLTEyLjc2LDcuMzVILjY1bC0uNjUtMy4yM0w2Ljc2LjA2bDcuMjMtLjA2WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:28.17,h:23.52},
  'potato-wedge':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMi45NCAzOC4xMSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0zLjUzLDM1LjA1TDAsMTguNTgsMyw3LjE3LDkuNzYsMGgzLjE4bC0zLjgyLDM4LjExYy0xLjY1LS4xOC00LjQ3LTEtNS41OS0zLjA2WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:12.94,h:38.11},
  'potato':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMy43IDM4LjE3Ij4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTTEwLjc2LDBoNC44MmMzLjg4LDEuMjMsNi4yOSw1LjY1LDguMTIsMTIuODhsLS4xMiw1LjQxLTEuODIsMy41My0zLjEyLDEwLjQxYy0yLjg4LDQuNDctNy4wNiw1Ljk0LTguODgsNS45NHMtNC4wNi0uODItNS4yOS0zLjEyTDAsMjIuNzYsMy43Niw0LjM1LDEwLjc2LDBaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:23.7,h:38.17},
  'pumpkin-chunk':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNS4xMSAyMy42NCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMC4xNywxLjc2bDEwLjA2LTEuNzYsMy44Miw1LjQ3LDEuMDYsMTQuNDctMTIuODIsMy43MWMtNS4wNi0uNDEtMTAuMTctMS41My0xMi4yOS0zLjU5TDIuMzUsMi40MSw0LjE4LjA2bDYsMS43MVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:25.11,h:23.64},
  'pumpkin':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1My4wNCA0Ny4wNSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMi43NiwzLjUzbDEtLjM1LDkuMjksMy40MS0uODItMy44Mi40MS0yLjA2YzIuMjkuNDcsNi42NS4wNiw4LjI5LS43MWw0LjQ3LDEuNDctMS40Nyw2YzMuMjMtLjc2LDYuMjMtMiw3Ljg4LTNsMy40NywxLjM1YzMuMTIsNC4xMiw1Ljg4LDcuODIsNi42NSwxMi4zNWwxLjEyLDExLjE3LS40MSwxLjk0LTkuMzUsMTEuNTMtMS4xOCwzLjEyYy0zLjctLjEyLTcuNy0xLjQxLTEwLjI5LTMuNTNsLTQuMzUsNC42NS05Ljg4LTIuNTljLTIuNDcuOTQtNi43Ljg4LTEwLjExLjI5LTIuNjUtMS42NS01LjEyLTYuNzYtNy40Ny0xNGwyLjE4LTEzLjc2TDEyLjc2LDMuNTNaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:53.04,h:47.05},
  'rice':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1LjU5IDIzLjUyIj4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTS43MSwyMS45NEwwLDIuNDEsMi45NC4zNWwuOTQtLjM1LDEuNzEsMi4xOC0uMjQsMTkuMTEtMS4zNSwyLjA2LS41OS4xOC0yLjcxLTEuNTlaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:5.59,h:23.52},
  'saffron':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2LjcgMjMuNTIiPgogIDxnIGlkPSJMYXllcl8xLTIiIGRhdGEtbmFtZT0iTGF5ZXIgMSI+CiAgICA8cGF0aCBkPSJNMi43Ni40N2wyLjI0LS40NywxLjcxLDguMjMtMy43Niw4LjU5YzEuNDcsMy4xOCwyLjc2LDMuMjksMy4wNiw2LjA2bC0xLjgyLjY1Yy0uNDEtMi41OS0yLjcxLTMuNDctNC4xOC02LjI5bC4xMi0uODhMMy44Miw3Ljg4LDIuNzYuNDdaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:6.7,h:23.52},
  'snake-gourd':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMi4zNiA2NC4zMyI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik00Ljg2LDMuMThsLjI5LS4xOC43MSwxLjM1Yy44OCwxLjQxLS43MSw0LjI5LS41OSwxNC44OC4xMiwxMC41OCwyLjA2LDIzLjcsMy40MSwyOS4zNHM0LjIzLDExLjUzLDMuNTksMTQuNThsLTEuNzEsMS4xOC03LTEzLjQxQy41LDQ0Ljg3LS41NSwzMi45OS4yNywyMS42NEwyLjc0LDQuMDZsLjcxLS40MWMtLjEyLTEuMjMtLjU5LTIuMzUtMS4xOC0zLjI5bC43Ni0uMzVjLjE4LjEyLDEuMjksMS43MSwxLjgyLDMuMThaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:12.36,h:64.33},
  'spinach':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMi45NiAzOC4xOCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0wLDM4LjA1bDMuNjUtMTUuMjMtLjk0LTIuNDFjLS40MS0xLjEyLS40Ny0yLjg4LjQxLTQuNy44OC0xLjgyLDUuMjktMTAuNTMsOS44OC0xMy42NGwzLjg4LTIuMDZjLjgyLjE4LDEuNjUuODIsMi40Ny44Mi41OSwwLDIuMTItLjQ3LDIuODgsMCwuOTQuNTksMS4xOCw0Ljc2LS41Myw5LjY0LTEuNzEsNC44OC03LjI5LDEwLjUzLTE1Ljg4LDEyLjgybC0xLjEyLDcuMjMsMS4xMiw2LjIzYy0uOTQuOTQtNC44MiwxLjc2LTUuODIsMS4yOVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:22.96,h:38.18},
  'star-anise':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMy44OCAyMy40MSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik05Ljk0LDBsMy45NCw2Ljc2LDIuNjUtMy44OCwzLjI5LS4zNS4yNCwxLjUzLTQuNDEsOCw3LjY1LDEuOTQuNTksMi40Ny03Ljc2LDEuMjMsMSw0LjM1LTMsMS4zNS00LjM1LTYuMzVjLTEuNDEsMS42NS0yLjgyLDQuMTgtNC4yOSw0Ljk0bC0yLjk0Ljg4LS42NS0yLjgyLDQuMDYtNy4xN2MtMS44OC0xLjM1LTQuNDEtMi41OS01LjctNGwtLjI0LTEuNDcsMi40Ny0uMzUuOTQtLjcxYzEuNzYuNjUsMy41OSwxLjk0LDQuNzYsMi0uNDEtMS4yOS0xLjcxLTUuNDctMS4zNS03Ljg4LjgyLDAsMi4xMiwwLDMuMTItLjQ3WiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:23.88,h:23.41},
  'stoneflower':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNC43NiAyMy41MiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik02LjQxLDBsMS40MSwxLjcxLjU5LS4yNGMuNjUuMjksMS4zNS40NywyLC40MWwuMTguNzZjLS44MiwxLjIzLTEuNDEsMi40Ny0xLjg4LDMuODhsMi43Ni41M2MuMTgsMS4yNC0uMTIsMi44Mi0uNDEsMy44Mi43Ni4zNSwyLjA2LDIuMTgsMi44MiwxLjk0bC4yOS43NmMtMS4yMywxLjM1LTIuMzUsMi4yOS0yLjgyLDMuODIsMS4xMiwxLjE4LDIuODIsMi40MSwzLjQxLDMuNjVsLTEuMTIsMS4xMi0xLjY1LjI5LTEuMjMsMS4wNi0uNTMtLjY1Yy0uOTQtMS41My0xLjUzLTQuMzUtMy4zNS03LjE3bC0xLjIzLDMuNDctLjE4LDEuMTItMi4yMy0uMThjLS4yNC0xLjA2LS45NC0yLjE4LTEuNDEtMy4yM2wuMDYtMS4xMiwxLjIzLTEuNzZ2LS43MWMtMS4yMy0xLjEyLTIuMDYtLjgyLTMtMS41OWwtLjEyLTEuMjksMS44Mi00LjE4LDIuNzEuMDYuOTQuNzEtLjE4LS43MS42NS0yLjM1Yy0uNTMtLjQ3LTEuNTktLjgyLTIuNDctLjc2bC0uMjQtLjc2TDQuNTMuMjlsMS44OC0uMjlaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:14.76,h:23.52},
  'sweet-potato':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMy4zNiAzOC4wNSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0yMC41OSwxMC40MWwyLjc2LDhjLjEyLDguNDctMy40NywxMi4wNi00LjU5LDE4LjIzbC0yLjM1LDEuNDEtMy4xOC0zLjU5LTIuNTMtNi45NC0uMzUtNi41My0yLjQ3LTUuNTlMLjI0LDMuMjlDLS4zNCwyLjQxLjI0LDEuMzUuODMuOTRjLjU5LS4zNSwxLjE4LS42NSwxLjY1LS45NCw2LjcuMTgsMTMuMjksNi4xNywxOC4xMSwxMC40MVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:23.36,h:38.05},
  'tamarind':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNi4xMSA0Ny4wNSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xLjY1LDQ3LjA1bC0xLjY1LTQuMTgsNS4xMi0uNTMtLjc2LTcuMzUsNC40Ny0xLS44OC00LjcuNjUtMS43Niw0Ljk0LTMuNDctMi44Mi00LjE4LS4xOC0xLjc2LDIuNDEtMy45NC00LjI5LTUuNDEtLjY1LTIuNzZMMTAuNzYsMGwxMSwyLjc2Yy40MSwyLjI5LjU5LDUuNTMuMjQsNy41OWw0LDUuMTcuMTIsMS4wNi0zLjQxLDYuMjksMi40MSw4LjQxYy0yLjcxLDIuNDctNC4zNSwzLjgyLTcuMzUsNS44MmwtLjI0LDUuNzYtMi4yMywxLjIzYy0yLjUzLDEuMjMtNy4xMi44OC04LjcuMTJsLTIuMjMsMi0yLjcxLjgyWiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:26.11,h:47.05},
  'taro':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMy4wOSAzOC40MyI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik05LjE2LDIuNTNsLjM1LTEuODIsMy4wNi0uNzEsMi43Ni43MWMuODguMjksMS4xOC40MSwxLjE4LDEuMTJ2LjUzbDEuNDcuMjksMy43LDVjMy44Miw2LTEuMTgsMjIuMTEtMi44OCwyNy41Mi00LjEyLDMuMjMtOS42NCwzLjU5LTEyLjgyLDMuMDYtMi4yOS0xLjcxLTQuOTQtNy4zNS01LjgyLTE3LjExLS4yOS0zLjM1LS40Ny00LjgyLDIuMjMtOS4zNUw3LjU3LDMuODhsLjA2LTEuMDZjLjQ3LS4xOCwxLjE4LS4zNSwxLjUzLS4yOVoiPjwvcGF0aD4KICA8L2c+Cjwvc3ZnPg==',w:23.09,h:38.43},
  'tomato-slice':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNi4yMyAyMy41OCI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0yNS41MiwxNC40N2wtMi41Myw3LjA2LTEwLjgyLDIuMDZjLTUsMC0xMC41OC0xLjk0LTEyLjE3LTMuNDcsMS4yOS0yLjIzLDQuNDEtNS41Myw4LjUzLTguNDdsMi0uMTgtLjA2LDMtMy41MywzLjU5YzEuNTMuNDEsMi44OC4zNSw1LjM1LS4xOGw2LjA2LTIuNDcsMy4xMi05LjQ3LTIuMTIsMS4xMi0yLjI0LDMuMjktMy40MS4wNi0uODgtMi4wNkwyMC4zNSwwbDIuODguMDZjMi4yMywyLjE4LDQuMDYsNi44OCwyLjI5LDE0LjQxWiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:26.23,h:23.58},
  'tomato':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzOS42MiAzOC4yMiI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xMC4xNywwbDQsLjY1Ljk0LDUuMzUuOTQuOTQsNC4zNS01Ljg4LDEuNzYuNTMuMzUsMy4xOCw0Ljg4LTMuMzUsNC4xOCwxLjI5LTEuNTMsNC4xOGMzLjM1LDEsNC43LjgyLDYuNzYsMy43LDEuMzUsMS45NCwxLjg4LDMuNDEsMi43MSw5LjIzLjc2LDUuMTgtMi44MiwxMC0xMS42NCwxNS4xMS02LjI5LDMuNTMtMTIsNC4yOS0xMi43NiwxLjk0LTQuMjMsMS41OS02LjQxLS40Ny03Ljk0LTIuNTMtMS41My0yLjEyLTMuODItOC4wNi00LjY1LTkuODgtLjk0LTEuODIuMjktNC41OSwxLjI5LTYuMDZsLTMuODItMSwuMjktMS4zNSwxMS03LjIzYy0uOTQtMi4xMi0xLjcxLTUuNjUtMS4xMi04LjgyWiI+PC9wYXRoPgogIDwvZz4KPC9zdmc+',w:39.62,h:38.22},
  'toor-split-dal':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA5LjcgMjMuNTIiPgogIDxnIGlkPSJMYXllcl8xLTIiIGRhdGEtbmFtZT0iTGF5ZXIgMSI+CiAgICA8cGF0aCBkPSJNLjc2LDBsMi45NC4wNiw1LjgyLDYuNjUuMTgsOS4xMmMtMS4wNiwyLjUzLTMuOTQsNi40Ny02LjUzLDcuN2wtMy4xOC0uNjVMLjc2LDBaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:9.7,h:23.52},
  'turnip':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyOS4zNCA2NC42OSI+CiAgPGcgaWQ9IkxheWVyXzEtMiIgZGF0YS1uYW1lPSJMYXllciAxIj4KICAgIDxwYXRoIGQ9Ik0xNy4zNSwyOS45M2MxLjgyLTUuMDYsMy42NS0xMC41OCw0LjI5LTE1LjgybDMuNTktMS40MSwzLjU5Ljk0LjUzLDEuODJjLTQuODIsNy41OS05LjY0LDIwLjY0LTkuNTksMjAuNDFsNS41OSwzLjgyYzIuNzYsMS45NCwxLjc2LDcuNDEsMSwxMS40Ny0uNjUsMi44Mi0yLjY1LDUuODItNy4zNSw2LjI5LTEuNzEsMS40MS0yLjc2LDMuMzUtMi45NCw3LjIzaC0zLjM1bC0xLTUuNDEtMS42NS0yLjI5LTMuMjMtMS41M2MtMi0uODItMi45NC0xLjcxLTMuNTMtMy43NmwtMi02LjI5Yy0uNTMtMS45NC40Ny00LDMuMzUtNi4yM2w0LjEyLTMuMTJMLjk0LDMwLjI5bC0uOTQtMS4yMy41My0yLjQxLDIuODItMS40MSw5LjI5LDcuODJjLTEuMTItMTAtMy41OS0yMC44Mi03LjQxLTI4LjY0TDkuMjMsMGw1Ljg4LjQ3LDIuMjMsMjkuNDZaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:29.34,h:64.69},
  'urad-split-dal':{uri:'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMiIgZGF0YS1uYW1lPSJMYXllciAyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA4LjM1IDIzLjUyIj4KICA8ZyBpZD0iTGF5ZXJfMS0yIiBkYXRhLW5hbWU9IkxheWVyIDEiPgogICAgPHBhdGggZD0iTS40NywxLjIzTDIuNjUsMGMzLjgyLDEuNzYsNS41OSw0LjM1LDUuNywxMS42NC0uMjksNi41My0yLjQ3LDkuNDctNC43NiwxMS4xMWwtMi44Mi43Ni0uNzYtMS4wNiwxLjc2LTEwLjdMLjQ3LDEuMjNaIj48L3BhdGg+CiAgPC9nPgo8L3N2Zz4=',w:8.35,h:23.52}
};
customElements.define('kuraco-particles', KuracoParticles);

/* ─────────────────────────────────────────── Header ─── */
class KuracoHeader extends HTMLElement {
  connectedCallback() {
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;height:1px;width:1px;pointer-events:none';
    document.body.prepend(sentinel);
    const header = this.querySelector('.kc-header');
    new IntersectionObserver(([e]) => {
      header.classList.toggle('is-stuck', !e.isIntersecting);
    }, { rootMargin: '0px' }).observe(sentinel);

    // Mobile drawer toggle
    const toggle  = this.querySelector('[data-drawer-toggle]');
    const drawer  = document.getElementById('kc-mobile-drawer');
    const backdrop= document.querySelector('[data-drawer-backdrop]');
    const closeBtn= drawer && drawer.querySelector('[data-drawer-close]');
    const openDrawer = () => {
      drawer.hidden = false; backdrop.hidden = false;
      requestAnimationFrame(() => { drawer.classList.add('is-open'); backdrop.classList.add('is-open'); });
      toggle.setAttribute('aria-expanded', 'true');
    };
    const closeDrawer = () => {
      drawer.classList.remove('is-open'); backdrop.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      setTimeout(() => { drawer.hidden = true; backdrop.hidden = true; }, 250);
    };
    toggle?.addEventListener('click', openDrawer);
    closeBtn?.addEventListener('click', closeDrawer);
    backdrop?.addEventListener('click', closeDrawer);
    drawer?.addEventListener('click', (e) => {
      if (e.target.closest('a')) closeDrawer();
    });
  }
}
customElements.define('kuraco-header', KuracoHeader);

/* ─────────────────────────────────────────── Cart popover ─── */
class KuracoCartPopover extends HTMLElement {
  connectedCallback() {
    this.pop = this.querySelector('#kc-mini-cart');
    this.trigger = this.querySelector('[data-cart-trigger]');
    if (!this.pop || !this.trigger) return;

    // Portal to body so clip-path on .kc-header doesn't clip the popover
    document.body.appendChild(this.pop);

    this._lastFocus = null;
    this._onDocClick = (e) => {
      if (this.pop.contains(e.target) || this.trigger.contains(e.target)) return;
      this.close();
    };
    this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
    this._onScroll = () => { if (Math.abs(window.scrollY - this._scrollAtOpen) > 80) this.close(); };

    this.trigger.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(); });
    this.pop.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
    this.pop.addEventListener('click', this.onPopoverClick.bind(this));

    kcEvents.on('cart:updated', (e) => this.render(e.detail));
    // Cart drawer no longer auto-opens on add — the cart-burst confetti
    // (kuraco-cart-burst.js) is the visual confirmation now. The drawer
    // still opens on direct click of the cart icon (line 340 above).
  }

  open() {
    if (this.pop.hidden === false && this.pop.classList.contains('is-open')) return;
    this._lastFocus = document.activeElement;
    this._scrollAtOpen = window.scrollY;
    const rect = this.trigger.getBoundingClientRect();
    this.pop.style.top = `${rect.bottom + 20}px`;
    if (window.innerWidth >= 768) {
      this.pop.style.right = `${window.innerWidth - rect.right - 8}px`;
      this.pop.style.left  = '';
      this.pop.style.width = '';
    } else {
      const hdr = document.querySelector('.kc-header');
      const hdrRect = hdr ? hdr.getBoundingClientRect() : null;
      this.pop.style.left  = hdrRect ? `${hdrRect.left}px`  : '12px';
      this.pop.style.right = hdrRect ? `${window.innerWidth - hdrRect.right}px` : '12px';
      this.pop.style.width = 'auto';
    }
    this.pop.hidden = false;
    requestAnimationFrame(() => this.pop.classList.add('is-open'));
    this.trigger.setAttribute('aria-expanded', 'true');
    this.pop.querySelector('[data-cart-close]')?.focus();
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  close() {
    if (this.pop.hidden) return;
    this.pop.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    setTimeout(() => { this.pop.hidden = true; }, 240);
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('scroll', this._onScroll);
    this._lastFocus?.focus?.();
  }

  disconnectedCallback() { this.pop?.remove(); }

  toggle() { this.pop.hidden ? this.open() : this.close(); }

  async onPopoverClick(e) {
    const dec = e.target.closest('[data-qty-dec]');
    const inc = e.target.closest('[data-qty-inc]');
    if (!dec && !inc) return;
    e.preventDefault();
    const li  = e.target.closest('[data-line-key]');
    if (!li) return;
    const key = li.dataset.lineKey;
    const cur = parseInt(li.querySelector('[data-qty]').textContent, 10) || 0;
    const next = Math.max(0, cur + (inc ? 1 : -1));
    const cart = await kcCartChange(key, next);
    kcEvents.emit('cart:updated', cart);
  }

  render(cart) {
    const items   = this.pop.querySelector('[data-cart-items]');
    const empty   = this.pop.querySelector('[data-cart-empty]');
    const total   = this.pop.querySelector('[data-cart-total]');
    const cta     = this.pop.querySelector('[data-cart-cta]');
    const subt    = this.pop.querySelector('[data-cart-subtotal]');
    const cnt     = this.pop.querySelector('[data-cart-count-text]');
    const noun    = this.pop.querySelector('[data-cart-noun]');

    cnt.textContent  = cart.item_count;
    noun.textContent = cart.item_count === 1 ? 'meal' : 'meals';
    subt.textContent = formatMoney(cart.total_price);

    // Update header badge
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.dataset.empty = cart.item_count === 0 ? 'true' : 'false';
    });

    // Items list — re-render line items
    items.innerHTML = '';
    cart.items.forEach((it) => {
      const li = document.createElement('li');
      li.className = 'kc-popover__item';
      li.dataset.lineKey = it.key;
      const img = it.image
        ? `<img src="${it.image.replace('{width}', '112') || it.image}" alt="${(it.product_title || '').replace(/"/g,'&quot;')}" width="56" height="56" loading="lazy">`
        : '';
      li.innerHTML = `
        <div class="kc-popover__thumb">${img}</div>
        <div>
          <p class="kc-popover__name">${it.product_title}</p>
          <div class="kc-qty">
            <button type="button" data-qty-dec aria-label="Decrease">−</button>
            <span data-qty>${it.quantity}</span>
            <button type="button" data-qty-inc aria-label="Increase">+</button>
          </div>
        </div>
        <span class="kc-popover__price" data-line-price>${formatMoney(it.final_line_price)}</span>
      `;
      items.appendChild(li);
    });

    const isEmpty = cart.item_count === 0;
    empty.hidden = !isEmpty;
    total.hidden = isEmpty;
    cta.hidden   = isEmpty;
  }
}
customElements.define('kuraco-cart-popover', KuracoCartPopover);

/* ─────────────────────────────────────────── Product card add-to-cart ─── */
class KuracoProductCard extends HTMLElement {
  async connectedCallback() {
    const btn     = this.querySelector('.kc-card__add[data-variant-id]');
    const qtyEl   = this.querySelector('.kc-card__qty');
    const qtyNum  = this.querySelector('.kc-card__qty-num');
    const decBtn  = this.querySelector('[data-dec]');
    const incBtn  = this.querySelector('[data-inc]');
    if (!btn || !qtyEl) return;

    const variantId = btn.dataset.variantId;
    let lineKey = null;
    let qty = 0;

    const findItem = (cart) => cart.items.find(i => String(i.variant_id) === String(variantId));

    const setLoading = (v) => {
      btn.disabled = v; btn.classList.toggle('is-loading', v);
      if (decBtn) decBtn.disabled = v;
      if (incBtn) incBtn.disabled = v;
    };

    const showQty = (q, key) => {
      qty = q; lineKey = key;
      qtyNum.textContent = q;
      btn.hidden = true;
      qtyEl.hidden = false;
    };

    const showAdd = () => {
      qty = 0; lineKey = null;
      btn.hidden = false;
      qtyEl.hidden = true;
    };

    // Restore state from current cart on page load
    try {
      const cart = await kcCartFetch();
      const item = findItem(cart);
      if (item) showQty(item.quantity, item.key);
    } catch {}

    btn.addEventListener('click', async (e) => {
      e.preventDefault(); e.stopPropagation();
      if (btn.disabled) return;
      setLoading(true);
      try {
        const cart = await kcCartAdd(variantId, 1);
        kcEvents.emit('cart:updated', cart);
        kcEvents.emit('cart:add-success', cart);
        const item = findItem(cart);
        if (item) showQty(item.quantity, item.key);
        else showQty(1, null);
      } catch (err) {
        console.error('[KuraCo] cart add failed', err);
      } finally {
        setLoading(false);
      }
    });

    incBtn?.addEventListener('click', async (e) => {
      e.preventDefault(); e.stopPropagation();
      setLoading(true);
      try {
        const cart = await kcCartAdd(variantId, 1);
        kcEvents.emit('cart:updated', cart);
        const item = findItem(cart);
        if (item) showQty(item.quantity, item.key);
      } catch (err) {
        console.error('[KuraCo] cart inc failed', err);
      } finally {
        setLoading(false);
      }
    });

    decBtn?.addEventListener('click', async (e) => {
      e.preventDefault(); e.stopPropagation();
      if (!lineKey) return;
      setLoading(true);
      try {
        const newQty = qty - 1;
        const cart = await kcCartChange(lineKey, newQty);
        kcEvents.emit('cart:updated', cart);
        if (newQty <= 0) {
          showAdd();
        } else {
          const item = findItem(cart);
          if (item) showQty(item.quantity, item.key);
          else showAdd();
        }
      } catch (err) {
        console.error('[KuraCo] cart dec failed', err);
      } finally {
        setLoading(false);
      }
    });
  }
}
customElements.define('kuraco-product-card', KuracoProductCard);

/* ─────────────────────────────────────────── PDP wiring ─── */
class KuracoProductPage extends HTMLElement {
  connectedCallback() {
    this.variants = this.parseVariants();
    this.bindThumbs();
    this.bindOptions();
    this.bindQuantity();
    this.bindForm();
  }

  parseVariants() {
    const el = this.querySelector('[data-variant-json]');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch { return []; }
  }

  bindThumbs() {
    const upgrade = (src) => src.replace(/([?&])width=\d+/, '$1width=1600');
    const cache = new Map();
    const preload = (src) => {
      if (cache.has(src)) return cache.get(src);
      const im = new Image();
      im.src = src;
      cache.set(src, im);
      return im;
    };

    const prefetchAll = () => {
      this.querySelectorAll('[data-thumb] img').forEach(img => {
        const src = img.currentSrc || img.src;
        if (src) preload(upgrade(src));
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(prefetchAll, { timeout: 1500 });
    else setTimeout(prefetchAll, 400);

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-thumb]');
      if (!btn || !this.contains(btn)) return;
      const main = this.querySelector('.kc-pdp__main-img');
      if (!main) return;
      const img = btn.querySelector('img');
      if (!img) return;
      e.preventDefault();
      const hi = upgrade(img.currentSrc || img.src);
      preload(hi);
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
      main.src = hi;
      main.dataset.mediaId = btn.dataset.mediaId;
      this.querySelectorAll('[data-thumb]').forEach(t => t.removeAttribute('aria-current'));
      btn.setAttribute('aria-current', 'true');
    });
  }

  bindOptions() {
    this.querySelectorAll('[data-option-input]').forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });
  }

  bindQuantity() {
    const dec = this.querySelector('.kc-qty--lg [data-qty-dec]');
    const inc = this.querySelector('.kc-qty--lg [data-qty-inc]');
    const input = this.querySelector('[data-pdp-qty]');
    const formQty = this.querySelector('[data-pdp-form-qty]');
    if (!input) return;
    const sync = () => { if (formQty) formQty.value = input.value; };
    dec?.addEventListener('click', () => { input.value = Math.max(1, (+input.value || 1) - 1); sync(); });
    inc?.addEventListener('click', () => { input.value = (+input.value || 1) + 1; sync(); });
    input.addEventListener('input', sync);
  }

  bindForm() {
    const form = this.querySelector('[data-pdp-form]');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn  = form.querySelector('[data-pdp-add]');
      const id   = form.querySelector('[data-pdp-variant-id]').value;
      const qty  = +form.querySelector('[data-pdp-form-qty]').value || 1;
      btn.disabled = true; btn.classList.add('is-loading');
      try {
        const cart = await kcCartAdd(id, qty);
        kcEvents.emit('cart:updated', cart);
        kcEvents.emit('cart:add-success', cart);
      } catch (err) {
        console.error('[KuraCo] add failed', err);
      } finally {
        btn.disabled = false; btn.classList.remove('is-loading');
      }
    });
  }

  updateVariant() {
    const inputs = [...this.querySelectorAll('[data-option-input]:checked')];
    const selected = inputs.map(i => i.value);
    const match = this.variants.find(v =>
      v.options.length === selected.length && v.options.every((o, i) => o === selected[i])
    );
    if (!match) return;

    const variantField = this.querySelector('[data-pdp-variant-id]');
    const addBtn       = this.querySelector('[data-pdp-add]');
    const priceEl      = this.querySelector('[data-pdp-price]');
    const labelPrice   = this.querySelector('[data-pdp-add-price]');
    const labelWrap    = this.querySelector('[data-pdp-add-label]');

    variantField.value     = match.id;
    addBtn.dataset.variantId = match.id;
    addBtn.disabled         = !match.available;

    if (priceEl) {
      let html = '';
      if (match.compare_at_price && match.compare_at_price > match.price) {
        html += `<del>${formatMoney(match.compare_at_price)}</del> `;
      }
      html += formatMoney(match.price);
      priceEl.innerHTML = html;
    }
    if (labelPrice) labelPrice.textContent = formatMoney(match.price);
    if (labelWrap)  labelWrap.firstChild && (labelWrap.innerHTML =
      match.available
        ? `Add to bag &middot; <span data-pdp-add-price>${formatMoney(match.price)}</span>`
        : 'Sold out'
    );

    // Update URL with variant query for share-ability
    const url = new URL(window.location.href);
    url.searchParams.set('variant', match.id);
    history.replaceState({}, '', url);
  }
}
customElements.define('kuraco-product-page', KuracoProductPage);
